import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROUTES, STORAGE_KEYS } from '../utils/constants';
import { GoogleLogin } from '@react-oauth/google';
import api from '../services/api';
import SuccessModal from '../components/common/SuccessModal';

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      setShowSuccess(true);
      
      // Get redirect path from location state, default to dashboard
      const redirectTo = location.state?.from || ROUTES.DASHBOARD;
      
      setTimeout(() => {
        navigate(redirectTo);
      }, 2000);
    } catch (err) {
      setError(err.message || 'فشل تسجيل الدخول. تحقق من البريد الإلكتروني وكلمة المرور');
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      setError('');
      const response = await api.googleLogin(credentialResponse.credential, 'login');
      
      if (response.token && response.user) {
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.token);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.user));
        setShowSuccess(true);
        
        // Get redirect path from location state, default to dashboard
        const redirectTo = location.state?.from || ROUTES.DASHBOARD;
        
        setTimeout(() => {
          window.location.href = redirectTo;
        }, 2000);
      }
    } catch (err) {
      setError(err.message || 'فشل تسجيل الدخول باستخدام Google');
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('فشل تسجيل الدخول باستخدام Google. حاول مرة أخرى');
  };

  return (
    <div className="relative z-10 py-20 bg-bg min-h-screen flex items-center overflow-x-hidden">
      {showSuccess && (
        <SuccessModal message="مرحبا! تم تسجيل دخولك بنجاح في Allemni" />
      )}
      <div className="container mx-auto px-4">
        <div className="max-w-[500px] mx-auto">
          <div className="bg-white rounded-3xl shadow-custom-lg p-10">
            <div className="text-center mb-8">
              <div className="mx-auto mb-6 flex justify-center">
                <img 
                  src="/RIGHTALLEMNI.png" 
                  alt="Allemni - علمني" 
                  className="h-20 w-auto"
                />
              </div>
              <h1 className="text-3xl font-black text-text mb-2">مرحبا بعودتك لـ Allemni!</h1>
              <p className="text-text3">سجل دخولك باش تكمل التعلم</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-600 text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-text font-bold mb-2">البريد الإلكتروني</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-bg border-2 border-border rounded-xl text-text outline-none focus:border-primary transition-colors"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label className="block text-text font-bold mb-2">كلمة المرور</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-bg border-2 border-border rounded-xl text-text outline-none focus:border-primary transition-colors"
                  placeholder="••••••••"
                />
                <div className="mt-2 text-left">
                  <Link to={ROUTES.FORGOT_PASSWORD} className="text-sm text-primary hover:underline">
                    نسيت كلمة المرور؟
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-8 py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary-dark transition-all shadow-custom hover:shadow-custom-lg disabled:opacity-50"
              >
                {loading ? 'جاري التسجيل...' : 'تسجيل الدخول'}
              </button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-text3">أو</span>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  text="signin_with"
                  locale="ar"
                  width="380"
                />
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-text3">
                ما عندكش حساب؟{' '}
                <Link to={ROUTES.REGISTER} className="text-primary font-bold hover:underline">
                  سجل الآن
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
